import debounce from 'lodash/debounce';
import isEmpty from 'lodash/isEmpty';
import { StateCreator } from 'zustand';

import { fetchDelay } from './constants/fetchDelay';
import { maxResults } from './constants/maxResults';
import { USStates } from './constants/states';
import { deduplicateSuggestions } from './functions/deduplicateSuggestions';
import { isPoBox } from './functions/isPoBox';
import { AddressSuggestionResponse } from './types/AddressSuggestionResponse';
import { AddressSuggestionState } from './types/AddressSuggestionState';
import { AddressSuggestionStoreDependencies } from './types/AddressSuggestionStoreDependencies';

export const addressSuggestionStore =
  ({
    key,
    useApiStore,
  }: AddressSuggestionStoreDependencies): StateCreator<AddressSuggestionState> =>
  (set, get) => ({
    activeFieldName: null,
    addressSuggestions: [],
    isFetchingAddressSuggestions: false,
    selectedIndex: -1,

    computed: {
      get hasAddressSuggestions(): boolean {
        const { addressSuggestions } = get();

        return !isEmpty(addressSuggestions);
      },
      get validStates(): string {
        return USStates.map((state): string => state.abbreviation).join(';');
      },
    },

    fetchAddressSuggestions: debounce(
      async (search: string, fieldName: string): Promise<void> => {
        const { isFetchingAddressSuggestions, getSearchParams } = get();

        if (isFetchingAddressSuggestions) {
          return;
        }

        set({ activeFieldName: fieldName, isFetchingAddressSuggestions: true });

        const params = getSearchParams(search);
        const httpRequest = useApiStore.getState().httpRequest();

        await httpRequest
          .get<AddressSuggestionResponse>(
            `https://us-autocomplete-pro.api.smartystreets.com/lookup?${params}`,
          )
          .then(({ data: { suggestions } }): void => {
            // Filter out PO boxes and remove duplicates
            const nonPoBoxSuggestions = suggestions.filter(
              (suggestion): boolean => !isPoBox(suggestion),
            );
            const uniqueSuggestions =
              deduplicateSuggestions(nonPoBoxSuggestions);

            set({ addressSuggestions: uniqueSuggestions });
          })
          .catch((error: unknown): void => {
            console.warn('Error fetching address suggestions:', error);
          })
          .finally((): void => {
            set({ isFetchingAddressSuggestions: false });
          });
      },
      fetchDelay,
    ),

    getSearchParams: (search: string): URLSearchParams => {
      const {
        computed: { validStates },
      } = get();

      return new URLSearchParams({
        include_only_states: validStates,
        key,
        max_results: maxResults,
        search,
      });
    },

    setSelectedIndex: (index: number): void => {
      set({ selectedIndex: index });
    },

    clearSuggestions: (): void => {
      set({
        activeFieldName: null,
        addressSuggestions: [],
        selectedIndex: -1,
      });
    },
  });
